// §3.6's audited manual transition as the §4.7 detail offers it —
// `POST /v1/orders/{id}/override-status` (spec 09 §3.6, §5; D-288 rulings 5,
// 8, 18). Pure, no React, unit-tested over every status in
// __tests__/order-override-kernel.test.ts.
//
// THE ARCS ARE THE ONES §3.6 GRANTS A PERSON, and no others (ruling 5):
//
//   confirmed, in_production        → ready     "manual override by facility_admin"
//   confirmed, in_production, ready → shipped   D-286 ruling 11's remedy for a
//                                               short-shipped order
//   delivered                       → closed    "manual close by facility_admin"
//
// `→ in_production` is module 04's fact (a released work order), and a forced
// one would tell a customer their order is being made when nothing is.
// `→ delivered` and `→ cancelled` have their own doors (Mark delivered, the
// cancel). `shipped → closed` is not admitted: closure passes through
// `delivered`. Nothing is forced out of `pending_approval` (the decide is its
// door) or out of `cancelled` / `closed` (terminal).
//
// `→ shipped` NEEDS REAL SHIPPED GOODS AND NO OPEN SHIPMENT (ruling 18). With
// nothing shipped, Mark delivered would later flip zero shipments and close an
// order whose customer received nothing, so the server answers 422
// ORDER_NOTHING_SHIPPED and the remedy is the cancel. With a `picking` or
// `packed` shipment on the order — an EMPTY one included, exactly as the
// server's SHIPMENT_OPEN counts it — a shipped order would strand its picks,
// so the server answers 409 SHIPMENT_OPEN and the storekeeper ships or cancels
// it first.
//
// AND NO LINE STILL IN PRODUCTION (ruling 21). A line `in_production` has a
// live work order in module 04; forcing the order to `shipped` over it would
// tell the customer the goods left while the plant is still making them, so
// the server answers 409 ORDER_HAS_ACTIVE_WOS and the work orders are
// cancelled in production first.
//
// All three facts are on the §4.7 payload (`lines[].qty_shipped`,
// `lines[].line_status`, `shipments[].status`), so the select never offers the
// target the press would be refused on. The server re-checks every press
// under the header lock; this only decides what is offered (D-040's two
// layers).

/** The three statuses a person may force an order to. The contract's
 * `OrderOverrideStatusRequest.target_status` takes ANY order status, so that
 * a target another door owns is refused 422 STATUS_OVERRIDE_NOT_ALLOWED
 * naming that door rather than 400 at the schema. The select offers only
 * these three, each only from a status ruling 5 admits it from. */
export type OverrideTarget = "ready" | "shipped" | "closed";

/** Which targets each status may be forced to, before ruling 18's facts. */
const ARCS: Readonly<Record<string, readonly OverrideTarget[]>> = {
  confirmed: ["ready", "shipped"],
  in_production: ["ready", "shipped"],
  ready: ["shipped"],
  delivered: ["closed"],
};

/** `numeric(14,3)` text that is zero — never `Number()` on a quantity. */
function isZeroQty(qty: string): boolean {
  return /^0+(\.0+)?$/.test(qty);
}

/** What the arc rule reads off the §4.7 detail. */
export interface OverrideFacts {
  readonly status: string;
  readonly lines: readonly { readonly qty_shipped: string; readonly line_status: string }[];
  readonly shipments: readonly { readonly status: string }[];
}

/** The three conditions for `→ shipped`: some line carries shipped quantity
 * and no shipment is still open (ruling 18), and no line is still in
 * production (ruling 21). */
export function shippedOverrideAllowed(order: Pick<OverrideFacts, "lines" | "shipments">): boolean {
  const somethingShipped = order.lines.some((line) => !isZeroQty(line.qty_shipped));
  const shipmentOpen = order.shipments.some(
    (shipment) => shipment.status === "picking" || shipment.status === "packed",
  );
  const lineInProduction = order.lines.some((line) => line.line_status === "in_production");
  return somethingShipped && !shipmentOpen && !lineInProduction;
}

/** The targets the order may be forced to FROM its current status, in the
 * §3.6 order. Empty means the control is not offered at all. */
export function overrideTargets(order: OverrideFacts): OverrideTarget[] {
  const arcs = ARCS[order.status] ?? [];
  return arcs.filter((target) => target !== "shipped" || shippedOverrideAllowed(order));
}
